/**
 * One native question that pauses a run until the user answers.
 *
 * The model calls this alone when two deliverables would differ and neither
 * the workbook nor the usual banker standard decides. The answer comes back
 * as this tool's result, and the same run continues. `complete_run` is not
 * involved.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";

/** Model-facing tool name for a single user question. */
export const ASK_USER_TOOL_NAME = "ask_user";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;
const MAX_QUESTION_CHARS = 500;
const MAX_LABEL_CHARS = 160;
const MAX_TYPED_ANSWER_CHARS = 500;

/** One choice shown on the question card. */
export interface UserQuestionOption {
  /** Stable id the model uses to mark the recommendation. */
  id: string;
  /** User-facing label. */
  label: string;
}

/** A validated question waiting for the user. */
export interface UserQuestion {
  /** The one decision the run cannot make. */
  question: string;
  /** Two to four mutually exclusive choices. */
  options: UserQuestionOption[];
  /** Id of the option a banker would pick if the user says nothing more. */
  recommendedOptionId: string;
}

/** The user's choice, either a listed option or their own text. */
export interface UserQuestionAnswer {
  /** Option label, or the typed text when `optionId` is null. */
  answer: string;
  /** Selected option id. Null when the user typed an answer. */
  optionId: string | null;
}

/**
 * Native tool definition for one user question.
 *
 * Typed answers are always available, so the schema has no extra flag.
 */
export const ASK_USER_TOOL: ChartCapabilityToolDef = {
  name: ASK_USER_TOOL_NAME,
  description:
    "Pause the run and ask the user one question. Use this only when two materially different deliverables remain and neither the workbook nor the usual banker standard decides. Provide two to four options and set recommendedOptionId to the option you would choose. Call it alone, with no other tool. Do not use it for a question you can decide, and do not use it to finish the run.",
  inputSchema: {
    type: "object",
    properties: {
      question: {
        type: "string",
        description: "The one decision the user needs to make.",
      },
      options: {
        type: "array",
        minItems: MIN_OPTIONS,
        maxItems: MAX_OPTIONS,
        items: {
          type: "object",
          properties: {
            id: { type: "string", description: "Short stable id for this option." },
            label: { type: "string", description: "User-facing choice." },
          },
          required: ["id", "label"],
          additionalProperties: false,
        },
      },
      recommendedOptionId: {
        type: "string",
        description: "Id of the option you recommend.",
      },
    },
    required: ["question", "options", "recommendedOptionId"],
    additionalProperties: false,
  },
  strict: true,
};

/**
 * Validate model arguments for one user question.
 *
 * @param rawArguments - JSON arguments from the model tool call.
 * @returns The question to show, or an error the model can correct.
 */
export function parseAskUserArguments(
  rawArguments: string,
): { question: UserQuestion } | { error: string } {
  let value: unknown;
  try {
    value = JSON.parse(rawArguments) as unknown;
  } catch {
    return { error: "Arguments must be valid JSON." };
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { error: "Arguments must be an object." };
  }
  const record = value as Record<string, unknown>;
  const unknownKey = Object.keys(record).find(
    (key) => key !== "question" && key !== "options" && key !== "recommendedOptionId",
  );
  if (unknownKey) return { error: `Unknown ask_user field: ${unknownKey}.` };

  const question = typeof record.question === "string" ? record.question.trim() : "";
  if (!question) return { error: "question is required." };
  if (question.length > MAX_QUESTION_CHARS) {
    return { error: `question must be at most ${MAX_QUESTION_CHARS} characters.` };
  }
  if (!Array.isArray(record.options)) return { error: "options must be an array." };
  if (record.options.length < MIN_OPTIONS || record.options.length > MAX_OPTIONS) {
    return { error: `options must contain ${MIN_OPTIONS} to ${MAX_OPTIONS} choices.` };
  }

  const options: UserQuestionOption[] = [];
  const seenIds = new Set<string>();
  for (let index = 0; index < record.options.length; index += 1) {
    const rawOption = record.options[index];
    if (!rawOption || typeof rawOption !== "object" || Array.isArray(rawOption)) {
      return { error: `options[${index}] must be an object.` };
    }
    const optionRecord = rawOption as Record<string, unknown>;
    const id = typeof optionRecord.id === "string" ? optionRecord.id.trim() : "";
    const label = typeof optionRecord.label === "string" ? optionRecord.label.trim() : "";
    if (!id) return { error: `options[${index}].id is required.` };
    if (!label) return { error: `options[${index}].label is required.` };
    if (label.length > MAX_LABEL_CHARS) {
      return { error: `options[${index}].label must be at most ${MAX_LABEL_CHARS} characters.` };
    }
    if (seenIds.has(id)) return { error: `Duplicate option id "${id}".` };
    seenIds.add(id);
    options.push({ id, label });
  }

  const recommendedOptionId =
    typeof record.recommendedOptionId === "string" ? record.recommendedOptionId.trim() : "";
  if (!seenIds.has(recommendedOptionId)) {
    return { error: "recommendedOptionId must match one option id." };
  }
  return { question: { question, options, recommendedOptionId } };
}

/**
 * Turn a card submission into the tool result the model reads.
 *
 * A listed option uses that option's label, ignoring any other text. A typed
 * answer is accepted only when it is non-empty and within the card limit.
 *
 * @param question - The question that was shown.
 * @param answer - The option or typed text submitted from the card.
 * @returns Serialized tool output, or an error when the submission does not match the question.
 */
export function formatAskUserResult(
  question: UserQuestion,
  answer: UserQuestionAnswer,
): { output: string } | { error: string } {
  if (answer.optionId) {
    const selected = question.options.find((option) => option.id === answer.optionId);
    if (!selected) return { error: "The selected option is not on this question." };
    return {
      output: JSON.stringify({ ok: true, answer: selected.label, optionId: selected.id }),
    };
  }
  const typed = answer.answer.trim();
  if (!typed) return { error: "A typed answer is required when no option is selected." };
  if (typed.length > MAX_TYPED_ANSWER_CHARS) {
    return { error: `A typed answer must be at most ${MAX_TYPED_ANSWER_CHARS} characters.` };
  }
  return { output: JSON.stringify({ ok: true, answer: typed, optionId: null }) };
}
